import { BaseError } from 'make-error';
import { ResultAsync, err, ok, okAsync } from 'neverthrow';

const scriptureClientErrorSymbol = Symbol("ScriptureClientError");
const scriptureFetchErrorSymbol = Symbol("ScriptureFetchError");
const scriptureBadDataErrorSymbol = Symbol("ScriptureBadDataError");
const scriptureHttpErrorSymbol = Symbol("ScriptureHttpError");
/**
 * The root of the custom error hierarchy for scripture-client.
 *
 * Only use this for operational errors. For the distinction between operation errors and programmer errors, read:
 * https://www.joyent.com/node-js/production/design/errors
 *
 * For 'programmer errors', just use the normal Error class, and let the program crash.
 */
class ScriptureClientError extends BaseError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureClientError = scriptureClientErrorSymbol;
    }
}
/**
 * The fetch method failed.
 */
class ScriptureFetchError extends ScriptureClientError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureFetchError = scriptureFetchErrorSymbol;
    }
}
/**
 * We got bad data from the server.
 */
class ScriptureBadDataError extends ScriptureClientError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureBadDataError = scriptureBadDataErrorSymbol;
    }
}
/**
 * We got an unexpected HTTP status code.
 */
class ScriptureHttpError extends ScriptureClientError {
    constructor(message, response, responseBody) {
        super(message);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureHttpError = scriptureHttpErrorSymbol;
        this.response = response;
        this.responseBody = responseBody;
    }
}

function expectJsonResponse(f, url, init = {}) {
    return ResultAsync.fromPromise(f(url, init), (e) => {
        // This could be a network issue, etc. Remember that fetch does not throw for >=400 status codes.
        // This is an operational error, not a programmer error, so do not throw.
        return new ScriptureFetchError((e === null || e === void 0 ? void 0 : e.message) || `Unknown fetch error for ${url}.`);
    }).andThen((response) => {
        if (!response.ok) {
            // Response is not in the 2XX range.
            return getHttpErrorResult(response);
        }
        return getJsonResponseResult(response);
    });
}
/**
 * Given a Response object with a response code outside the 2XX range, get a failing result object containing the error.
 */
function getHttpErrorResult(response) {
    // Sometimes the response body contains more useful info about the error.
    return ResultAsync.fromPromise(response.text(), (e) => e)
        .orElse(() => err(new ScriptureHttpError(response.statusText, response, "Failed to parse response body.")))
        .andThen((errorMessage) => err(new ScriptureHttpError(response.statusText, response, errorMessage)));
}
/**
 * Given a Response object with a response code in the 2XX range, get a result object representing the JSON data.
 */
function getJsonResponseResult(response) {
    return ResultAsync.fromPromise(response.json(), (e) => {
        // This could be a network issue, or the back end return invalid JSON.
        // This is an operational error, not a programmer error, so do not throw.
        return new ScriptureBadDataError((e === null || e === void 0 ? void 0 : e.message) || `Failed to get response body for ${response.url}.`);
    });
}

class ScriptureClient {
    /**
     * Create a ScriptureClient object.
     *
     * @param baseUrl
     *   The base URL of the Solr service.
     * @param f
     *   The `fetch` function.
     */
    constructor(baseUrl, f) {
        this.baseUrl = baseUrl;
        this.f = f;
    }
    /** @inheritdoc */
    getAllTranslations() {
        // TODO: Check if the data from the server actually satisfies the return type.
        return expectJsonResponse(this.f, new URL("/static/translations/_all.json", this.baseUrl).toString());
    }
    /** @inheritdoc */
    getTranslationIds() {
        return expectJsonResponse(this.f, new URL("/static/translations.json", this.baseUrl).toString());
    }
    /** @inheritdoc */
    getTranslation(tid) {
        return expectJsonResponse(this.f, new URL(`/static/translations/${tid}.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getBooks(tid) {
        return expectJsonResponse(this.f, new URL(`/static/books/${tid}.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getBook(tid, bid) {
        return expectJsonResponse(this.f, new URL(`/static/books/${tid}/${bid}.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getChapters(tid, bid) {
        return expectJsonResponse(this.f, new URL(`/static/chapters/${tid}/${bid}.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getChapter(tid, bid, chapter) {
        return expectJsonResponse(this.f, new URL(`/static/chapters/${tid}/${bid}/${chapter}.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getVerseIds(tid, bid, chapter) {
        return expectJsonResponse(this.f, new URL(null == chapter
            ? `/static/verses/${tid}/${bid}.ids.json`
            : `/static/verses/${tid}/${bid}/${chapter}.ids.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getVerseLabels(tid, bid, chapter) {
        return expectJsonResponse(this.f, new URL(`/static/verses/${tid}/${bid}/${chapter}.labels.json`, this.baseUrl).toString());
    }
    getVerseIdsAndLabels(tid, bid, chapter) {
        return expectJsonResponse(this.f, new URL(`/static/verses/${tid}/${bid}/${chapter}.ids-and-labels.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getVerseById(tid, vid) {
        return expectJsonResponse(this.f, new URL(`/static/verses/${tid}/${vid}.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getVerseByReference(tid, bid, chapter, verseLabel) {
        return expectJsonResponse(this.f, new URL(`/static/verses/${tid}/${bid}/${chapter}/${verseLabel}.json`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getVersesByIdRange(tid, fromVid, toVid) {
        if (fromVid == toVid) {
            // For a single verse, use a faster method.
            return this.getVerseById(tid, fromVid)
                .map((verse) => [verse])
                .orElse((e) => {
                // If this was a 404 response, return an empty array instead of an error.
                if (e instanceof ScriptureHttpError && e.response.status === 404) {
                    return ok([]);
                }
                return err(e);
            });
        }
        return expectJsonResponse(this.f, new URL(`/verses?tid=${tid}&fromVid=${fromVid}&toVid=${toVid}`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getVersesStartingAtId(tid, fromVid, limit) {
        // Don't call `getVerseById` here, because `fromVid` might be an ID between two actual verses.
        return expectJsonResponse(this.f, new URL(`/verses?tid=${tid}&fromVid=${fromVid}&limit=${limit}`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    getVersesEndingAtId(tid, toVid, limit) {
        // Don't call `getVerseById` here, because `toVid` might be an ID between two actual verses.
        return expectJsonResponse(this.f, new URL(`/verses?tid=${tid}&toVid=${toVid}&limit=${limit}`, this.baseUrl).toString());
    }
    /** @inheritdoc */
    findBookContainingVerse(tid, vid) {
        return this.getBooks(tid).map((books) => { var _a; return (_a = books.find((book) => book.fromVid <= vid && book.toVid >= vid)) === null || _a === void 0 ? void 0 : _a.bid; });
    }
    /** @inheritdoc */
    findChapterContainingVerse(tid, bid, vid) {
        return this.getChapters(tid, bid).map((chapters) => {
            var _a;
            return (_a = chapters.find((chapter) => chapter.fromVid <= vid && chapter.toVid >= vid)) === null || _a === void 0 ? void 0 : _a.number;
        });
    }
    /** @inheritdoc */
    findBookAndChapterContainingVerse(tid, vid) {
        return this.findBookContainingVerse(tid, vid).andThen((bid) => {
            if (!bid) {
                return okAsync(undefined);
            }
            return this.findChapterContainingVerse(tid, bid, vid).map((chapter) => {
                if (!chapter) {
                    return undefined;
                }
                return { bid, chapter };
            });
        });
    }
    /** @inheritDoc */
    search(q) {
        var _a, _b;
        const url = new URL("/search", this.baseUrl);
        url.searchParams.set("text", q.text);
        if ((_a = q.tids) === null || _a === void 0 ? void 0 : _a.size) {
            url.searchParams.set("tids", [...q.tids].join(","));
        }
        if ((_b = q.bids) === null || _b === void 0 ? void 0 : _b.size) {
            url.searchParams.set("bids", [...q.bids].join(","));
        }
        if (q.size) {
            url.searchParams.set("size", q.size.toString());
        }
        if (q.page) {
            url.searchParams.set("page", q.page.toString());
        }
        if (q.sortBy) {
            url.searchParams.set("sortBy", q.sortBy.toString());
        }
        return expectJsonResponse(this.f, url.toString());
    }
}

export { ScriptureBadDataError, ScriptureClient, ScriptureClientError, ScriptureFetchError, ScriptureHttpError };
